import "./Admin.scss";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import Heading from "./partials/Heading";
import { isEqual } from "lodash";
import cn from "classnames";
import {
  deleteMessage,
  adminLogout,
  messagesSelectors,
  useAppDispatch,
  selectAuthorInfo,
  useAppSelector,
  featuredLinksSelectors,
} from "../store";
import {
  createFeaturedLink,
  deleteFeaturedLink,
  updateAuthorInfo,
  updateFeaturedLink,
} from "../store/sagas/author-profile";
import { EntityId } from "@reduxjs/toolkit";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { pageVariants } from "../App";
import { createPortal } from "react-dom";

function MessagesFrame() {
  const messages = useAppSelector(messagesSelectors.selectAll);
  const dispatch = useAppDispatch();
  // console.log(messages);

  return (
    <div className="messages-container">
      <div className="messages-header">messages</div>
      {(messages.length &&
        messages.map((item, index) => {
          return (
            <div className="message-card" key={index}>
              <div className="sender-info">
                <div className="sender-name">FROM: {item.senderName}</div>
                <div className="sender-email-add">
                  <a href={`mailto:${item.senderEmailAdd}`}>
                    EMAIL: {item.senderEmailAdd}
                  </a>
                </div>
              </div>
              <div className="message">{item.message}</div>
              <div className="button-group">
                <button
                  onClick={() => dispatch(deleteMessage(item.id))}
                  className="negative"
                >
                  DELETE MESSAGE
                </button>
              </div>
            </div>
          );
        })) || <div className="no-message">You have no messages.</div>}
    </div>
  );
}

function FeaturedLinkForm({
  id,
  newItem,
}: {
  id: EntityId;
  newItem?: boolean;
}) {
  const featuredLink = useAppSelector((state) =>
    featuredLinksSelectors.selectById(state, id)
  );
  const [iconSrc, setIconSrc] = useState(
    newItem ? "" : featuredLink?.iconSrc ?? ""
  );
  const [name, setName] = useState(newItem ? "" : featuredLink?.name ?? "");
  const [href, setHref] = useState(newItem ? "" : featuredLink?.href ?? "");
  const [text, setText] = useState(newItem ? "" : featuredLink?.text ?? "");
  const [external, setExternal] = useState(featuredLink?.external ?? false);
  const [showOnHome, setShowOnHome] = useState(
    featuredLink?.showOnHome ?? false
  );

  const dispatch = useAppDispatch();

  const matches = isEqual(featuredLink, {
    id,
    name,
    iconSrc,
    href,
    text,
    external,
    showOnHome,
  });

  const clearFields = useCallback(() => {
    setName("");
    setExternal(false);
    setShowOnHome(false);
    setText("");
    setHref("");
    setIconSrc("");
  }, []);

  const resetFields = useCallback(() => {
    if (featuredLink) {
      setName(featuredLink.name);
      setExternal(featuredLink.external);
      setShowOnHome(featuredLink.showOnHome);
      setText(featuredLink.text);
      setHref(featuredLink.href);
      setIconSrc(featuredLink.iconSrc);
    } else {
      clearFields();
    }
  }, [featuredLink, clearFields]);

  // a bit of hack but what it does it resets the fields to thew newly arrived data
  // from the database after an update
  useEffect(() => {
    resetFields();
  }, [resetFields]);

  const noFieldIsEmpty = href && name && text && iconSrc;

  return (
    <form
      onSubmit={(ev) => {
        if (newItem) {
          dispatch(
            createFeaturedLink({
              external,
              href,
              iconSrc,
              name,
              showOnHome,
              text,
            })
          );
          // clearFields();
        } else if (!matches) {
          dispatch(
            updateFeaturedLink({
              id,
              external,
              href,
              iconSrc,
              name,
              showOnHome,
              text,
            })
          );
          // resetFields();
        }

        ev.preventDefault();
      }}
      id={`featured-link-${id}`}
    >
      {name && <div className="header">{name}</div>}
      <label htmlFor={`name-${id}`}>
        NAME
        <input
          id={`name-${id}`}
          name="name"
          required
          value={name}
          onChange={(ev) => setName(ev.target.value)}
        />
      </label>
      <label htmlFor={`text-${id}`}>
        TEXT
        <input
          id={`text-${id}`}
          name="text"
          required
          value={text}
          onChange={(ev) => setText(ev.target.value)}
        />
      </label>
      <label htmlFor={`iconSrc-${id}`}>
        ICON SRC
        <input
          id={`iconSrc-${id}`}
          name="iconSrc"
          required
          value={iconSrc}
          onChange={(ev) => setIconSrc(ev.target.value)}
        />
      </label>
      <label htmlFor={`href-${id}`}>
        HREF
        <input
          id={`href-${id}`}
          name="href"
          required
          value={href}
          onChange={(ev) => setHref(ev.target.value)}
        />
      </label>
      <div className="checkbox-group">
        <label htmlFor={`external-${id}`}>
          EXTERNAL
          <input
            id={`external-${id}`}
            name="external"
            type="checkbox"
            checked={external}
            onChange={(ev) => setExternal(ev.target.checked)}
          />
        </label>
        <label htmlFor={`showOnHome-${id}`}>
          SHOW ON HOME
          <input
            id={`showOnHome-${id}`}
            name="showOnHome"
            type="checkbox"
            checked={showOnHome}
            onChange={(ev) => setShowOnHome(ev.target.checked)}
          />
        </label>
      </div>

      <div className="button-group">
        {!newItem && (
          <button
            className={cn("positive", {
              disabled: matches || !noFieldIsEmpty,
            })}
          >
            APPLY CHANGES
          </button>
        )}

        {newItem && (
          <button
            className={cn("positive", {
              // disabled: isEqualInfo || !noFieldIsEmpty,
            })}
          >
            SAVE
          </button>
        )}
        <button
          className="negative"
          onClick={(ev) => {
            clearFields();
            ev.preventDefault();
          }}
        >
          CLEAR
        </button>

        {!newItem && (
          <button
            className="negative"
            onClick={(ev) => {
              dispatch(deleteFeaturedLink(id));
              ev.preventDefault();
            }}
          >
            DELETE
          </button>
        )}
        {!newItem && (
          <button
            className="neutral"
            onClick={(ev) => {
              resetFields();
              ev.preventDefault();
            }}
          >
            RESET
          </button>
        )}
      </div>
    </form>
  );
}

function EditFeaturedLink() {
  const featuredLinks = useAppSelector(featuredLinksSelectors.selectIds);

  return (
    <div className="edit-featured-link">
      <FeaturedLinkForm id="" newItem />
      {featuredLinks.length &&
        featuredLinks.map((item, index) => (
          <FeaturedLinkForm id={item} key={index} />
        ))}
    </div>
  );
}

function EditInfo() {
  const authorInfo = useAppSelector(selectAuthorInfo);
  const [name, setName] = useState(authorInfo.name);
  const [title, setTitle] = useState(authorInfo.title);
  const [displayPic, setDisplayPic] = useState(authorInfo.displayPic);
  const [resumeHref, setResumeHref] = useState(authorInfo.resumeHref);
  const [aboutDesc, setAboutDesc] = useState(authorInfo.aboutDesc);
  const dispatch = useAppDispatch();
  const matches = isEqual(authorInfo, {
    title,
    displayPic,
    resumeHref,
    aboutDesc,
    name,
    id: authorInfo.id,
  });
  const noFieldIsEmpty = name && title && displayPic && resumeHref && aboutDesc;

  return (
    <form
      onSubmit={(ev) => {
        if (!matches) {
          dispatch(
            updateAuthorInfo({
              aboutDesc,
              displayPic,
              id: authorInfo.id,
              name,
              resumeHref,
              title,
            })
          );
        }

        ev.preventDefault();
      }}
    >
      <div className="header">SET AUTHOR INFO</div>
      <label htmlFor="name">
        NAME
        <input
          id="name"
          name="name"
          required
          value={name}
          onChange={(ev) => setName(ev.target.value)}
        />
      </label>
      <label htmlFor="title">
        TITLE
        <input
          id="title"
          name="title"
          required
          value={title}
          onChange={(ev) => setTitle(ev.target.value)}
        />
      </label>
      <label htmlFor="displayPic">
        DISPLAY PICTURE URL
        <input
          id="displayPic"
          name="displayPic"
          required
          value={displayPic}
          onChange={(ev) => setDisplayPic(ev.target.value)}
        />
      </label>
      <label htmlFor="resumeHref">
        RESUME URL
        <input
          id="resumeHref"
          name="resumeHref"
          required
          value={resumeHref}
          onChange={(ev) => setResumeHref(ev.target.value)}
        />
      </label>
      <label htmlFor="aboutDesc">
        ABOUT SECTION DESCRIPTION
        <textarea
          id="aboutDesc"
          name="aboutDesc"
          required
          value={aboutDesc}
          onChange={(ev) => setAboutDesc(ev.target.value)}
        />
      </label>
      <div className="button-group">
        <button
          className={cn("positive", {
            disabled: matches || !noFieldIsEmpty,
          })}
        >
          APPLY CHANGES
        </button>
        <button
          className="negative"
          onClick={(ev) => {
            setName("");
            setTitle("");
            setAboutDesc("");
            setDisplayPic("");
            setResumeHref("");
            setDisplayPic("");
            ev.preventDefault();
          }}
        >
          CLEAR
        </button>
        <button
          className="neutral"
          onClick={(ev) => {
            setName(authorInfo.name);
            setTitle(authorInfo.title);
            setAboutDesc(authorInfo.aboutDesc);
            setDisplayPic(authorInfo.displayPic);
            setResumeHref(authorInfo.resumeHref);
            ev.preventDefault();
          }}
        >
          RESET
        </button>
      </div>
    </form>
  );
}

interface Cb {
  (): void;
}

const imgBtnVariants: Variants = {
  tap: {
    scale: 0.9,
  },
  hover: {
    opacity: 0.5,
    cursor: "pointer",
  },
};

interface ListItemGenericProps {
  text?: string;
  onAdd?: Cb;
  onMinus?: Cb;
  onDelete?: Cb;
  onEdit?: Cb;
}

function ListItemGeneric({
  text,
  onAdd,
  onMinus,
  onDelete,
  onEdit,
}: ListItemGenericProps) {
  return (
    <div className="list-item-generic">
      {(text && <p className="text">{text}</p>) || (
        <p className="nothing text">Nothing...</p>
      )}
      {onAdd && (
        <motion.img
          variants={imgBtnVariants}
          src="./assets/images/add-positive.svg"
          alt="add"
          whileTap={"tap"}
          whileHover={"hover"}
        />
      )}
      {onEdit && (
        <motion.img
          src="./assets/images/edit-neutral.svg"
          alt="edit"
          variants={imgBtnVariants}
          whileTap={"tap"}
          whileHover={"hover"}
        />
      )}
      {onMinus && (
        <motion.img
          variants={imgBtnVariants}
          src="./assets/images/minus-negative.svg"
          alt="minus"
          whileTap={"tap"}
          whileHover={"hover"}
        />
      )}
      {onDelete && (
        <motion.img
          src="./assets/images/trash-negative.svg"
          alt="trash"
          variants={imgBtnVariants}
          whileTap={"tap"}
          whileHover={"hover"}
        />
      )}
    </div>
  );
}

interface ListGenericProps extends Omit<ListItemGenericProps, "text"> {
  listNameKey: string;
  list?: any[];
  headerText?: string;
}
function ListGeneric({
  listNameKey,
  list,
  headerText,
  ...rest
}: ListGenericProps) {
  if (!list || list.length === 0) {
    return (
      <div className="list-generic">
        {headerText && <h2 className="small-header">{headerText}</h2>}
        <ListItemGeneric />
      </div>
    );
  } else {
    return (
      <div className="list-generic">
        {headerText && <h2 className="small-header">{headerText}</h2>}
        {list.map((item, index) => (
          <ListItemGeneric {...rest} text={item[listNameKey]} />
        ))}
      </div>
    );
  }
}
interface NameModalProps {
  onOk?: (newName: string) => void;
  text: string;
  oldName?: string;
  onCancel?: (newName: string) => void;
}

function NameModal({ text, oldName, onOk, onCancel }: NameModalProps) {
  const [value, setValue] = useState(oldName ?? "");
  return (
    <form className="name-modal" onSubmit={(ev) => ev.preventDefault()}>
      <h2 className="header">{text}</h2>
      <input
        required
        value={value}
        placeholder="Name"
        onChange={(ev) => setValue(ev.target.value)}
      />

      <div className="button-group">
        <button className="positive" onClick={() => onOk?.(value)}>
          OK
        </button>
        <button className="negative" onClick={() => onCancel?.(value)}>
          CANCEL
        </button>
      </div>
    </form>
  );
}

interface SelectExistingGenericModal {
  text: string;
}

function SelectExistingGeneric({ text }: SelectExistingGenericModal) {
  return (
    <div className="select-existing-modal ui-portion">
      <h2 className="header">{text}</h2>
      <div className="split">
        <div>
          <ListGeneric listNameKey="" headerText="select" />
        </div>
        <div>
          <ListGeneric listNameKey="" headerText="to add" />
        </div>
      </div>
      <div className="button-group">
        <button className="positive">OK</button>
        <button className="negative">CANCEL</button>
      </div>
    </div>
  );
}
interface UpsertToolSetProps {
  toolSetId?: EntityId;
  oldName?: string;
}
function UpsertToolSet({ toolSetId, oldName }: UpsertToolSetProps) {
  const [value, setValue] = useState(oldName ?? "");
  return (
    <div className="select-existing-modal ui-portion">
      <h2 className="header">
        {toolSetId ? "EDIT EXISTING" : " ADD NEW"} TOOL SET
      </h2>
      <input
        required
        value={value}
        placeholder="Name"
        onChange={(ev) => setValue(ev.target.value)}
      />
      <div className="split">
        <div>
          <ListGeneric listNameKey="" headerText="select" />
        </div>
        <div>
          <ListGeneric listNameKey="" headerText="to add" />
        </div>
      </div>
      <div className="button-group">
        <button>ADD EXISTING</button>
        <button>ADD NEW</button>
      </div>
      <div className="button-group">
        <button className="positive">OK</button>
        <button className="negative">CANCEL</button>
      </div>
    </div>
  );
}

function ToolsList() {
  return (
    <div className="tools-list ui-portion list-container">
      <h2 className="header">TOOL LIST</h2>
      <ListGeneric listNameKey="" />
      <motion.img
        className="add-button"
        src="./assets/images/add.svg"
        alt="add"
        variants={imgBtnVariants}
        whileTap={"tap"}
        whileHover={"hover"}
      />
    </div>
  );
}

function ToolSetList() {
  return (
    <div className="tools-list ui-portion list-container">
      <h2 className="header">TOOL SET LIST</h2>
      <ListGeneric listNameKey="" />
      <motion.img
        className="add-button"
        src="./assets/images/add.svg"
        alt="add"
        variants={imgBtnVariants}
        whileTap={"tap"}
        whileHover={"hover"}
      />
    </div>
  );
}

function EditToolList() {
  return (
    <div className="edit-tool-list">
      <ToolSetList />
      <ToolsList />
      <NameModal text="Edit Name" />
      <SelectExistingGeneric text="Select Existing Tool" />
      <UpsertToolSet />
    </div>
  );
}

function EditProjects() {
  return <div>edit projects</div>;
}

const settingsList: { [c: string]: { cb?: () => void; iconSrc: string } } = {
  "EDIT INFO": {
    iconSrc: "/assets/images/info.svg",
    cb: undefined,
  },
  "EDIT TOOL LIST": {
    iconSrc: "/assets/images/tool.svg",
    cb: undefined,
  },
  "EDIT FEATURED LINKS": {
    iconSrc: "/assets/images/link.svg",
    cb: undefined,
  },
  "EDIT PROJECTS": {
    iconSrc: "/assets/images/briefcase.svg",
    cb: undefined,
  },
  LOGOUT: {
    iconSrc: "/assets/images/logout.svg",
    cb: undefined,
  },
};

interface AdminSettingsProps {
  onSelect?: (renderProp: ReactNode) => void;
}
function AdminSettingsList({ onSelect }: AdminSettingsProps) {
  const dispatch = useAppDispatch();
  settingsList["LOGOUT"].cb = useCallback(() => {
    dispatch(adminLogout());
  }, [dispatch]);
  settingsList["EDIT PROJECTS"].cb = useCallback(() => {
    onSelect?.(<EditProjects />);
  }, [onSelect]);
  settingsList["EDIT INFO"].cb = useCallback(() => {
    onSelect?.(<EditInfo />);
  }, [onSelect]);
  settingsList["EDIT TOOL LIST"].cb = useCallback(() => {
    onSelect?.(<EditToolList />);
  }, [onSelect]);
  settingsList["EDIT FEATURED LINKS"].cb = useCallback(() => {
    onSelect?.(<EditFeaturedLink />);
  }, [onSelect]);

  return (
    <div className="nav-list">
      {Object.entries(settingsList).map(([key, item], index) => {
        return (
          <div
            key={index}
            className="nav-container"
            onClick={() => {
              item.cb?.();
            }}
          >
            <div className="nav-icon">
              <img src={item.iconSrc} alt="icon"></img>
            </div>
            <div className="nav-text">{key}</div>
          </div>
        );
      })}
    </div>
  );
}

interface ModalProps {
  children?: ReactNode;
  visible?: boolean;
  onClose?: (arg1: boolean) => void;
}
const modalVariants: Variants = {
  open: { y: 0, opacity: 1, visibility: "visible" },
  close: {
    y: "-10%",
    opacity: 0,
    visibility: "hidden",
    transition: { visibility: { delay: 0.15 } },
  },
};

const body = document.querySelector("body");

function Modal({ children, visible, onClose }: ModalProps) {
  return createPortal(
    <div>
      <AnimatePresence>
        <motion.div
          className="modal-container"
          initial="close"
          animate={visible ? "open" : "close"}
          variants={modalVariants}
        >
          {children}
          <img
            src="/assets/images/exit.svg"
            alt="exit"
            className="icon-img"
            onClick={(ev) => onClose?.(true)}
          />
        </motion.div>
      </AnimatePresence>
    </div>,
    body as HTMLBodyElement
  );
}
const html = document.querySelector("html");
interface Props {}
function Admin(props: Props) {
  const [closed, setClosed] = useState(true);
  const [modalChild, setModalChild] = useState<ReactNode>(<></>);

  useEffect(() => {
    if (html) {
      if (!closed) {
        html.style.overflow = "hidden";
      } else {
        html.style.overflow = "auto";
      }
    }
  }, [closed]);

  return (
    <motion.div
      className="admin-page"
      variants={pageVariants}
      initial="initial"
      animate="in"
      exit="out"
    >
      <Heading>Admin</Heading>
      <div className="content">
        <MessagesFrame />
        <AdminSettingsList
          onSelect={(ev) => {
            setModalChild(ev);
            setClosed(false);
          }}
        />
      </div>
      <Modal visible={!closed} onClose={setClosed}>
        {modalChild}
      </Modal>
    </motion.div>
  );
}
export { Admin as default };
